import { c as _c } from "react/compiler-runtime";
import makeBlockie from "ethereum-blockies-base64";
import { useMemo } from "react";
import { Image } from "../../icons";
import { objectFitVariants } from "../../styles/utils/objectFit";
import { classNames } from "../../utils";
import { CenterAligned } from "../CenterAligned";
import { AvatarBadge } from "./AvatarBadge";
import { generateAvatarBadgeClipPath } from "./avatar-badge";
const isIcon = props => {
  return props.icon !== undefined;
};

// Filters a className string based on prefix
// E.g. filterClassName("mx-3 rounded-xl", "rounded") -> "rounded-xl"
const filterClassName = (className, prefix) => {
  return classNames(className.split(" ").filter(c => c.startsWith(prefix)));
};
export const isVideo = props => {
  return [".webm", ".mp4", ".m4v", ".ogg", ".ogv", ".mov"].some(ext => props.src?.toLowerCase().endsWith(ext));
};
function ImgAvatar(t0) {
  const $ = _c(11);
  let renderImage;
  let rest;
  let seed;
  let src;
  if ($[0] !== t0) {
    ({
      renderImage,
      seed,
      src,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = renderImage;
    $[2] = rest;
    $[3] = seed;
    $[4] = src;
  } else {
    renderImage = $[1];
    rest = $[2];
    seed = $[3];
    src = $[4];
  }
  const Comp = renderImage ?? "img";
  let t1;
  bb0: {
    if (seed) {
      let t2;
      if ($[5] !== seed) {
        t2 = makeBlockie(seed);
        $[5] = seed;
        $[6] = t2;
      } else {
        t2 = $[6];
      }
      t1 = t2;
      break bb0;
    }
    t1 = undefined;
  }
  const dataUrl = t1;
  const t2 = src || dataUrl;
  let t3;
  if ($[7] !== Comp || $[8] !== rest || $[9] !== t2) {
    t3 = <Comp {...rest} src={t2} />;
    $[7] = Comp;
    $[8] = rest;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function VideoAvatar(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <video {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
const DEFAULT_ICON_SIZE = 24;
export function Avatar(t0) {
  const $ = _c(25);
  let badge;
  let border;
  let borderClassName;
  let props;
  let propsClassName;
  let renderImage;
  let rounded;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      className: propsClassName,
      badge,
      renderImage,
      rounded,
      size: t1,
      border,
      borderClassName,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = badge;
    $[2] = border;
    $[3] = borderClassName;
    $[4] = props;
    $[5] = propsClassName;
    $[6] = renderImage;
    $[7] = rounded;
    $[8] = style;
    $[9] = t1;
  } else {
    badge = $[1];
    border = $[2];
    borderClassName = $[3];
    props = $[4];
    propsClassName = $[5];
    renderImage = $[6];
    rounded = $[7];
    style = $[8];
    t1 = $[9];
  }
  const size = t1 === undefined ? DEFAULT_ICON_SIZE : t1;
  const setDefaultSize = size === DEFAULT_ICON_SIZE && (isIcon(props) ? !props.overrides?.Icon?.size : true);
  const t2 = rounded && "rounded-full";
  const t3 = setDefaultSize && "size-6";
  let t4;
  let t5;
  if ($[10] !== badge || $[11] !== border || $[12] !== borderClassName || $[13] !== props || $[14] !== propsClassName || $[15] !== renderImage || $[16] !== size || $[17] !== style || $[18] !== t2 || $[19] !== t3) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const className = classNames("aspect-square overflow-hidden", t2, t3, propsClassName);
      const renderAvatar = t6 => {
        const assetStyles = t6 === undefined ? {} : t6;
        if (isVideo(props) && !("frameTime" in props)) {
          return <VideoAvatar className={className} size={size} style={assetStyles} {...props} />;
        }
        if (isIcon(props)) {
          const {
            icon: IconComponent
          } = props;
          return <IconComponent aria-label={props["aria-label"]} size={size} style={assetStyles} title={props.title} {...props.overrides?.Icon} className={classNames(props.overrides?.Icon?.className, className)} />;
        }
        if (props.children) {
          return <CenterAligned className={className} style={assetStyles} {...props} />;
        }
        const {
          objectFit: t7,
          src,
          seed,
          ...rest
        } = props;
        const objectFit = t7 === undefined ? "cover" : t7;
        if (!(renderImage || src || seed)) {
          return <CenterAligned className={classNames("bg-bg-additional-2", className)} style={assetStyles} {...props}><Image className="size-1/2" /></CenterAligned>;
        }
        return <ImgAvatar className={classNames(objectFitVariants({
          objectFit
        }), className)} renderImage={renderImage} seed={seed} size={size} src={src} style={assetStyles} {...rest} />;
      };
      const styles = {
        ...(size !== DEFAULT_ICON_SIZE && {
          width: `${size}px`,
          height: `${size}px`
        }),
        ...style
      };
      if (border || badge) {
        let t8;
        if ($[22] !== badge || $[23] !== size) {
          t8 = badge ? {
            clipPath: `path("${generateAvatarBadgeClipPath(size, badge.props.size)}")`
          } : {};
          $[22] = badge;
          $[23] = size;
          $[24] = t8;
        } else {
          t8 = $[24];
        }
        const badgeStyles = t8;
        t5 = <div className="relative inline-block shrink-0" style={styles}><div style={badgeStyles}>{renderAvatar(styles)}</div>{border && <div className={classNames("absolute inset-0", borderClassName ?? "inset-shadow-border", filterClassName(className, "rounded"))} style={badgeStyles} />}{badge}</div>;
        break bb0;
      }
      t4 = renderAvatar(styles);
    }
    $[10] = badge;
    $[11] = border;
    $[12] = borderClassName;
    $[13] = props;
    $[14] = propsClassName;
    $[15] = renderImage;
    $[16] = size;
    $[17] = style;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
  } else {
    t4 = $[20];
    t5 = $[21];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  return t4;
}
export { AvatarBadge };